import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  UserDisplayInfo,
  canChangePassword,
  ACCOUNT_DELETION_GRACE_DAYS,
  deleteOwnAccountConfirmedEmail,
  signOut,
  updateDisplayName,
  updatePasswordWithCurrentVerification,
} from '../services/authService';
import TeamClassSettingsSection from './TeamClassSettingsSection';
import UserProfileAvatar from './UserProfileAvatar';
import UserProfileImageUploadModal from './UserProfileImageUploadModal';
import {
  deleteUserProfileImage,
  uploadUserProfileImage,
} from '../services/userProfileImageStorageService';
import {
  invalidateUserProfileImageCache,
  putUserProfileImageCacheFromBlob,
} from '../utils/userProfileImageCache';
import { maskEmailForDisplay } from '../utils/loginEmailMask';

interface UserProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  userDisplay: UserDisplayInfo | null;
  workspaceId: string;
  onLogout: () => void;
  onUserDisplayUpdate: () => void;
  onDataChange: (options?: { selectGradeId?: string }) => void;
}

const UserProfileModal: React.FC<UserProfileModalProps> = ({
  isOpen,
  onClose,
  userDisplay,
  workspaceId,
  onLogout,
  onUserDisplayUpdate,
  onDataChange,
}) => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [displayName, setDisplayName] = useState(userDisplay?.displayName ?? '');
  const [showWithdrawConfirm, setShowWithdrawConfirm] = useState(false);
  const [profileMessage, setProfileMessage] = useState('');

  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordAllowed, setPasswordAllowed] = useState(true);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);

  const [withdrawConfirmEmail, setWithdrawConfirmEmail] = useState('');
  const [withdrawBusy, setWithdrawBusy] = useState(false);
  const [withdrawError, setWithdrawError] = useState('');
  const [withdrawImmediate, setWithdrawImmediate] = useState(false);
  const [profileImageModalOpen, setProfileImageModalOpen] = useState(false);
  const [profileImageBusy, setProfileImageBusy] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setDisplayName(userDisplay?.displayName ?? '');
      setProfileMessage('');
    }
  }, [isOpen, userDisplay?.displayName]);

  useEffect(() => {
    if (!passwordModalOpen) return;
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    void (async () => {
      const ok = await canChangePassword();
      setPasswordAllowed(ok);
    })();
  }, [passwordModalOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setPasswordModalOpen(false);
      setShowWithdrawConfirm(false);
      setProfileImageModalOpen(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!showWithdrawConfirm) return;
    setWithdrawConfirmEmail('');
    setWithdrawError('');
    setWithdrawBusy(false);
  }, [showWithdrawConfirm]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !passwordModalOpen && !showWithdrawConfirm && !profileImageModalOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, passwordModalOpen, showWithdrawConfirm, profileImageModalOpen]);

  const handleSaveProfileImage = async (blob: Blob) => {
    const uploaded = await uploadUserProfileImage(blob);
    await putUserProfileImageCacheFromBlob(uploaded, blob);
    setProfileMessage('프로필 이미지가 저장되었습니다.');
    onUserDisplayUpdate();
  };

  const handleDeleteProfileImage = async () => {
    const path = userDisplay?.profileImage?.path?.trim();
    if (!path || !userDisplay?.userId || profileImageBusy) return;
    setProfileImageBusy(true);
    try {
      await deleteUserProfileImage(path);
      await invalidateUserProfileImageCache(userDisplay.userId);
      setProfileMessage('프로필 이미지를 삭제했습니다.');
      onUserDisplayUpdate();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '이미지 삭제에 실패했습니다.';
      setProfileMessage(msg);
    } finally {
      setProfileImageBusy(false);
    }
  };

  const handleSaveName = async () => {
    if (!displayName.trim()) return;
    try {
      await updateDisplayName(displayName);
      setProfileMessage('이름이 저장되었습니다.');
      onUserDisplayUpdate();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '저장에 실패했습니다.';
      setProfileMessage(msg);
    }
  };

  const handleWithdrawConfirm = async () => {
    const accEmail = userDisplay?.email?.trim().toLowerCase() ?? '';
    const typed = withdrawConfirmEmail.trim().toLowerCase();
    if (!accEmail) {
      setWithdrawError('이 계정은 이메일로 식별되지 않아 여기서 탈퇴할 수 없습니다. 고객 지원으로 문의해 주세요.');
      return;
    }
    if (!typed || typed !== accEmail) {
      setWithdrawError('가입 시 사용한 이메일과 정확히 일치하게 입력해 주세요.');
      return;
    }
    setWithdrawBusy(true);
    setWithdrawError('');
    try {
      await deleteOwnAccountConfirmedEmail(typed, withdrawImmediate ? 'immediate' : 'grace');
      setShowWithdrawConfirm(false);
      await signOut();
      onLogout();
    } catch (e: unknown) {
      setWithdrawError(e instanceof Error ? e.message : '회원탈퇴에 실패했습니다.');
    } finally {
      setWithdrawBusy(false);
    }
  };

  const passwordFormValid =
    currentPassword.length > 0 &&
    newPassword.length >= 6 &&
    newPassword === confirmPassword;

  const handlePasswordSubmit = async () => {
    const email = userDisplay?.email?.trim();
    if (!email || !passwordFormValid || passwordSubmitting) return;
    setPasswordSubmitting(true);
    setPasswordError('');
    try {
      await updatePasswordWithCurrentVerification(email, currentPassword, newPassword);
      setPasswordModalOpen(false);
      setProfileMessage('비밀번호가 변경되었습니다.');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '비밀번호 변경에 실패했습니다.';
      setPasswordError(msg);
    } finally {
      setPasswordSubmitting(false);
    }
  };

  return createPortal(
    <div
      className={`cp-profile-modal-root${isOpen ? '' : ' cp-profile-modal-backdrop-hidden'}`}
      aria-hidden={!isOpen}
    >
      <div className="cp-profile-modal-dim" aria-hidden />
      <div
        className="cp-profile-modal-scroll-host"
        onMouseDown={(e) => {
          const target = e.target as Node | null;
          if (
            isOpen &&
            !passwordModalOpen &&
            !showWithdrawConfirm &&
            !profileImageModalOpen &&
            target &&
            !contentRef.current?.contains(target)
          ) {
            onClose();
          }
        }}
      >
        <div className="cp-profile-modal-scroll-inner">
          <div
            className="cp-profile-modal-content"
            ref={contentRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-profile-modal-title"
          >
            <div className="cp-profile-modal-header">
              <h2 id="cp-profile-modal-title" className="cp-profile-modal-title">
                프로필 설정
              </h2>
              <button
                type="button"
                className="cp-profile-modal-close"
                onClick={onClose}
                aria-label="닫기"
              >
                <img src="/modal-close.svg" alt="" width={18} height={18} />
              </button>
            </div>
            <div className="cp-profile-modal-inner">
              {profileMessage && (
                <p className="cp-profile-message">{profileMessage}</p>
              )}

              <section className="cp-profile-section">
                <h3>사용자 이름</h3>
                {userDisplay?.email?.trim() ? (
                  <p className="cp-profile-account-email">{maskEmailForDisplay(userDisplay.email)}</p>
                ) : null}
                <div className="cp-profile-avatar-pick">
                  <button
                    type="button"
                    className={`cp-profile-avatar-slot${userDisplay?.profileImage ? ' has-image' : ''}`}
                    onClick={() => setProfileImageModalOpen(true)}
                    aria-label={userDisplay?.profileImage ? '프로필 이미지 변경' : '프로필 이미지 등록'}
                  >
                    {userDisplay ? (
                      <UserProfileAvatar
                        userId={userDisplay.userId}
                        custom={
                          userDisplay.profileImage
                            ? { userId: userDisplay.userId, ...userDisplay.profileImage }
                            : null
                        }
                        googleAvatarUrl={null}
                        className="cp-profile-avatar-slot-img"
                      />
                    ) : (
                      <span className="cp-profile-avatar-slot-plus" aria-hidden>
                        +
                      </span>
                    )}
                  </button>
                  <div className="cp-profile-avatar-actions">
                    <button
                      type="button"
                      className="cp-btn-secondary cp-profile-avatar-action-btn"
                      onClick={() => setProfileImageModalOpen(true)}
                    >
                      {userDisplay?.profileImage ? '이미지 변경' : '이미지 등록'}
                    </button>
                    <button
                      type="button"
                      className="cp-btn-secondary cp-profile-avatar-action-btn"
                      onClick={() => void handleDeleteProfileImage()}
                      disabled={!userDisplay?.profileImage || profileImageBusy}
                    >
                      이미지 삭제
                    </button>
                  </div>
                </div>
                <div className="cp-profile-form-row">
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="사용자 이름"
                    className="cp-profile-input"
                  />
                  <button type="button" className="cp-btn-primary" onClick={handleSaveName}>
                    저장
                  </button>
                </div>
              </section>

              <TeamClassSettingsSection
                workspaceId={workspaceId}
                active={isOpen}
                onDataChange={onDataChange}
                onMembershipChange={onUserDisplayUpdate}
              />

              <section className="cp-profile-actions">
                <button
                  type="button"
                  className="cp-btn-primary cp-profile-password-open"
                  onClick={() => setPasswordModalOpen(true)}
                >
                  비밀번호 변경
                </button>
                <button
                  type="button"
                  className="cp-profile-withdraw-text"
                  onClick={() => setShowWithdrawConfirm(true)}
                >
                  회원탈퇴
                </button>
              </section>
            </div>
          </div>
        </div>
      </div>

      <UserProfileImageUploadModal
        open={profileImageModalOpen}
        onClose={() => setProfileImageModalOpen(false)}
        onSave={handleSaveProfileImage}
      />

      {passwordModalOpen && (
        <div
          className="cp-modal-backdrop cp-password-change-backdrop"
          style={{ zIndex: 3000 }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={() => {
            if (!passwordSubmitting) setPasswordModalOpen(false);
          }}
        >
          <div
            className="cp-modal cp-password-change-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-password-change-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="cp-password-change-title" className="cp-password-change-title">
              비밀번호 변경
            </h3>
            {!passwordAllowed ? (
              <p className="cp-password-change-hint">
                소셜 로그인만 사용 중인 계정은 비밀번호를 변경할 수 없습니다.
              </p>
            ) : (
              <>
                <div className="cp-password-change-field">
                  <label className="cp-password-change-label" htmlFor="cp-pw-current">
                    현재 비밀번호
                  </label>
                  <input
                    id="cp-pw-current"
                    type="password"
                    autoComplete="current-password"
                    className="cp-password-change-input"
                    placeholder="현재 비밀번호 입력"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    disabled={passwordSubmitting}
                  />
                </div>
                <div className="cp-password-change-field">
                  <label className="cp-password-change-label" htmlFor="cp-pw-new">
                    새 비밀번호
                  </label>
                  <input
                    id="cp-pw-new"
                    type="password"
                    autoComplete="new-password"
                    className="cp-password-change-input"
                    placeholder="6자 이상 입력"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={passwordSubmitting}
                  />
                </div>
                <div className="cp-password-change-field">
                  <label className="cp-password-change-label" htmlFor="cp-pw-confirm">
                    새 비밀번호 확인
                  </label>
                  <input
                    id="cp-pw-confirm"
                    type="password"
                    autoComplete="new-password"
                    className="cp-password-change-input"
                    placeholder="새 비밀번호를 다시 입력"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={passwordSubmitting}
                  />
                </div>
                {passwordError ? (
                  <p className="cp-password-change-error" role="alert">
                    {passwordError}
                  </p>
                ) : null}
              </>
            )}
            <div className="cp-password-change-footer">
              <button
                type="button"
                className="cp-btn-primary cp-password-change-submit"
                disabled={!passwordAllowed || !passwordFormValid || passwordSubmitting}
                onClick={() => void handlePasswordSubmit()}
              >
                {passwordSubmitting ? '처리 중…' : '비밀번호 변경'}
              </button>
              <button
                type="button"
                className="cp-btn-secondary cp-password-change-cancel"
                disabled={passwordSubmitting}
                onClick={() => setPasswordModalOpen(false)}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {showWithdrawConfirm && (
        <div
          className="cp-modal-backdrop"
          style={{ zIndex: 3000 }}
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.stopPropagation();
            setShowWithdrawConfirm(false);
          }}
        >
          <div className="cp-modal cp-profile-withdraw-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="cp-profile-withdraw-title">회원탈퇴</h3>
            <p className="cp-profile-withdraw-desc">
              탈퇴하면 바로 로그아웃되며, {ACCOUNT_DELETION_GRACE_DAYS}일 동안은 다시 로그인해 계정을 복구할 수 있습니다.{' '}
              {ACCOUNT_DELETION_GRACE_DAYS}일이 지나면 이 계정의 워크스페이스·팀·저장 데이터가 영구 삭제되어 복구할 수 없습니다. 계속하려면{' '}
              <strong>가입에 사용한 이메일 주소</strong>를 아래에 그대로 입력한 뒤 진행해 주세요.
            </p>
            {userDisplay?.email?.trim() ? (
              <div className="cp-profile-withdraw-field">
                <label className="cp-profile-withdraw-label" htmlFor="cp-profile-withdraw-email">
                  계정 이메일 확인
                </label>
                <input
                  id="cp-profile-withdraw-email"
                  type="email"
                  autoComplete="off"
                  className="cp-profile-withdraw-input"
                  placeholder={maskEmailForDisplay(userDisplay.email)}
                  value={withdrawConfirmEmail}
                  onChange={(e) => setWithdrawConfirmEmail(e.target.value)}
                  disabled={withdrawBusy}
                />
              </div>
            ) : (
              <p className="cp-profile-withdraw-warn" role="alert">
                이 계정에는 이메일 정보가 없어 이 화면에서 탈퇴할 수 없습니다.
              </p>
            )}
            <label className="cp-profile-withdraw-label" style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
              <input
                type="checkbox"
                checked={withdrawImmediate}
                onChange={(e) => setWithdrawImmediate(e.target.checked)}
                disabled={withdrawBusy}
              />
              <span>복구 기간 없이 즉시 영구삭제 (선택 시 바로 삭제되며 복구할 수 없습니다)</span>
            </label>
            {withdrawError ? (
              <p className="cp-profile-withdraw-error" role="alert">
                {withdrawError}
              </p>
            ) : null}
            <div className="cp-modal-footer cp-leave-modal-actions cp-profile-withdraw-footer">
              <button
                type="button"
                className="cp-btn-secondary"
                disabled={withdrawBusy}
                onClick={() => setShowWithdrawConfirm(false)}
              >
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                style={{ background: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}
                disabled={
                  withdrawBusy ||
                  !userDisplay?.email?.trim() ||
                  withdrawConfirmEmail.trim().toLowerCase() !== userDisplay.email.trim().toLowerCase()
                }
                onClick={() => void handleWithdrawConfirm()}
              >
                {withdrawBusy ? '처리 중…' : '회원탈퇴 진행'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

export default UserProfileModal;
